import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { ArrowRight, Landmark, Clock, X, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtA = (v) => `A$${Math.round(v || 0).toLocaleString()}`;

/** v380 — FX movement alerts vs each open estimate's FX snapshot (implementation view). */
export function FxMovementCard() {
  const [fx, setFx] = useState(null);
  const [showAcked, setShowAcked] = useState(false);
  const loadFx = () => {
    axios.get(`${API_URL}/api/fx/movement-alerts`, { withCredentials: true })
      .then(res => setFx(res.data))
      .catch(() => setFx(null));
  };

  const ackAlert = async (id, action) => {
    try {
      await axios.post(`${API_URL}/api/fx/movement-alerts/${id}/ack`,
        action === 'snooze' ? { action, days: 7 } : { action }, { withCredentials: true });
      toast.success(action === 'snooze' ? 'Alert snoozed for 7 days' : 'Alert dismissed — re-alerts if the move shifts ≥1pp');
      loadFx();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not acknowledge alert'); }
  };

  const restoreAlert = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/fx/movement-alerts/${id}/ack`, { withCredentials: true });
      toast.success('Alert restored');
      loadFx();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not restore alert'); }
  };

  const resnapshot = async (id) => {
    try {
      const r = await axios.post(`${API_URL}/api/fx/movement-alerts/${id}/resnapshot`, {}, { withCredentials: true });
      toast.success(`Re-snapshotted at today's rate — A$${Number(r.data.fx_rate_to_aud).toFixed(4)}`);
      loadFx();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not re-snapshot'); }
  };

  useEffect(() => { loadFx(); }, []);
  if (!(fx && fx.alert_threshold_pct != null)) return null;
  return (
        <Card className={fx.count > 0 ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'} data-testid="fx-movement-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <Landmark className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-[#0F2040]">FX movement</h3>
              <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center ${
                fx.count > 0 ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-500'}`}
                data-testid="fx-movement-count">{fx.count}</span>
            </div>
            <p className="text-[11px] text-slate-500 mb-3">
              Open estimates whose currency moved more than {fx.alert_threshold_pct}% against the FX rate snapped when they were saved. Leadership also receives a daily email digest.
            </p>
            {fx.count === 0 ? (
              <p className="text-xs text-slate-400" data-testid="fx-movement-empty">
                No open estimate has moved beyond the {fx.alert_threshold_pct}% threshold.
              </p>
            ) : (
              <div className="space-y-1.5">
                {fx.alerts.map(a => (
                  <div key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-amber-200 bg-white px-3 py-2"
                    data-testid={`fx-movement-row-${a.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-[#0F2040] truncate">{a.name}</div>
                      <div className="text-[10px] text-slate-400 truncate">{a.organization_name || '—'} · {a.currency_code}</div>
                    </div>
                    <div className="flex items-center gap-1 text-[11px] font-mono text-slate-600">
                      A${Number(a.snapshot_rate).toFixed(4)}
                      <ArrowRight className="w-3 h-3 text-slate-400" />
                      A${Number(a.current_rate).toFixed(4)}
                    </div>
                    <span className={`text-[11px] font-bold ${a.move_pct >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                      {a.move_pct >= 0 ? '+' : ''}{a.move_pct}%
                    </span>
                    <span className={`text-[11px] font-semibold ${a.aud_impact >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                      {a.aud_impact >= 0 ? '+' : '−'}{fmtA(Math.abs(a.aud_impact))}
                    </span>
                    <span className="flex items-center gap-0.5">
                      <button type="button" onClick={() => resnapshot(a.id)}
                        className="p-1 rounded text-slate-400 hover:text-emerald-700 hover:bg-slate-100"
                        title="Re-snapshot at today's rate (locks in the current rate and clears this alert)" data-testid={`fx-movement-resnapshot-${a.id}`}>
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => ackAlert(a.id, 'snooze')}
                        className="p-1 rounded text-slate-400 hover:text-[#0F2040] hover:bg-slate-100"
                        title="Snooze for 7 days" data-testid={`fx-movement-snooze-${a.id}`}>
                        <Clock className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => ackAlert(a.id, 'dismiss')}
                        className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-slate-100"
                        title="Dismiss (re-alerts if the move shifts ≥1pp)" data-testid={`fx-movement-dismiss-${a.id}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
            {fx.acknowledged_count > 0 && (
              <div className="mt-3">
                <button type="button" onClick={() => setShowAcked(s => !s)}
                  className="text-[11px] text-slate-500 underline hover:text-[#0F2040]"
                  data-testid="fx-movement-acked-toggle">
                  {fx.acknowledged_count} acknowledged {showAcked ? '▲' : '▼'}
                </button>
                {showAcked && (
                  <div className="space-y-1 mt-2">
                    {fx.acknowledged.map(a => (
                      <div key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 opacity-80"
                        data-testid={`fx-movement-acked-row-${a.id}`}>
                        <div className="min-w-0 flex-1">
                          <span className="text-xs font-medium text-slate-600 truncate">{a.name}</span>
                          <span className="text-[10px] text-slate-400 ml-2">
                            {a.ack?.action === 'snooze'
                              ? `snoozed until ${(a.ack.snoozed_until || '').slice(0, 10)}`
                              : 'dismissed'} · {a.ack?.by || ''}
                          </span>
                        </div>
                        <span className={`text-[11px] font-semibold ${a.move_pct >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                          {a.move_pct >= 0 ? '+' : ''}{a.move_pct}%
                        </span>
                        <button type="button" onClick={() => restoreAlert(a.id)}
                          className="text-[10px] text-[#4ABFCF] underline hover:text-[#0F2040]"
                          data-testid={`fx-movement-restore-${a.id}`}>restore</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
  );
}
